<template>
    <div class="body_costs_analysis">
        <div v-if="isLoading" class="state_message">
            <p>Cargando análisis…</p>
        </div>

        <div v-else-if="error" class="state_message">
            <p class="error_text">{{ error }}</p>
        </div>

        <div v-else-if="analysis" class="analysis_grid">
            <AccountStatementCard class="cell"
                title="Costos"
                :period-label="analysis.period_label"
                :headline="analysis.headline"
                :statement="analysis.statement"
                :selected-account="selectedAccount"
                :selected-level="selectedAccountLevel"
                @select-account="onSelectAccount" />

            <CounterpartsRankingCard class="cell"
                v-model="counterpartsQueryModel"
                :counterparts="filteredCounterparts"
                @counterpart-click="openCounterpartDetail" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import AccountStatementCard from "@/components/financialReports/AccountStatementCard.vue";
import CounterpartsRankingCard from "./CounterpartsRankingCard.vue";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import HeaderClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/HeaderClientDetail.vue";
import BodyClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/BodyClientDetail.vue";
import { obligationForType } from "@/utils/counterpartObligation";

export default {
    name: "BodyCostsAnalysis",
    components: {
        AccountStatementCard,
        CounterpartsRankingCard,
    },
    // Orquestador del feature: lee del módulo costsAnalysis y reparte slices a
    // las dos cards presentacionales. El modal pasa `bodyProps: null` — la
    // lectura es vía Vuex.
    computed: {
        ...mapState("costsAnalysis", {
            analysis: "data",
            isLoading: "isLoading",
            error: "error",
            analysisMonth: "month",
            selectedAccount: "account",
            selectedAccountLevel: "accountLevel",
        }),
        ...mapGetters("costsAnalysis", ["filteredCounterparts"]),
        // Bridge entre el v-model del ranking y el store global. El padre asume
        // el acople a Vuex; la card se queda pura.
        counterpartsQueryModel: {
            get() { return this.$store.state.costsAnalysis.counterpartsQuery; },
            set(value) { this.setCounterpartsQuery(value); },
        },
    },
    methods: {
        ...mapMutations("costsAnalysis", { setCounterpartsQuery: "SET_COUNTERPARTS_QUERY" }),
        ...mapActions("costsAnalysis", ["selectRankingAccount"]),
        ...mapActions("clientDetail", {
            openClientDetailAction: "openClientDetail",
            closeClientDetailAction: "closeClientDetail",
            changeClientRangePeriod: "changeRangePeriod",
        }),

        obligationForType,

        // La card del estado (card 1) eligió una cuenta: recorta el ranking a
        // esa cuenta sin recargar el modal. { account: null } = todas (Costos),
        // { account, level: "2" } = grupo L2, { account, level: "3" } = cuenta L3.
        onSelectAccount({ account, level }) {
            this.selectRankingAccount({ account, level });
        },

        openCounterpartDetail(counterpart) {
            if (!counterpart || !counterpart.name || !this.analysisMonth) return;

            const documentType = "P";

            this.openClientDetailAction({
                alias: counterpart.name,
                documentType,
                monthIso: this.analysisMonth,
                // Metadata pedida con la obligación del tipo clickeado, para
                // traer la contraparte correcta (Honorario/Trabajador/… quedan
                // fuera del bucket "Otros"; "all" mezclaría tipos distintos).
                obligationName: this.obligationForType(counterpart.counterpart_type),
                // Subject por level_1 ("Costos"): los level_2 de costos son
                // sub-tipos, filtrar por uno solo perdería el resto. El
                // denominador "% de Ventas" del chart sigue en level2Name.
                level1Name: "Costos",
                // Acota docs + serie al mismo tipo que muestra la card (el
                // ranking ya lo conoce), para que todo el modal sea coherente.
                counterpartType: counterpart.counterpart_type,
            });

            this.$buefy.modal.open({
                parent: this,
                hasModalCard: true,
                customClass: "wide-summary-modal",
                canCancel: ["escape", "outside"],
                component: ModalFullHeightRight,
                props: {
                    headerComp: HeaderClientDetail,
                    bodyComp: BodyClientDetail,
                    headerProps: { fallbackAlias: counterpart.name },
                    bodyProps: null,
                    // El theme del tab Costos se propaga al segundo drawer para
                    // que los tokens --report-* del subárbol respeten la paleta.
                    theme: "costos",
                    // Ancho menor que el primer drawer (85%) para que se perciba
                    // como un segundo nivel apilado encima del análisis del mes.
                    width: "70%",
                },
                events: {
                    closed: this.closeClientDetailAction,
                    "update-range-period": this.changeClientRangePeriod,
                },
            });
        },
    },
};
</script>

<style scoped lang="scss">
// Cadena de altura: el body absorbe lo que le da .modal_back_caa (flex:1) y lo
// propaga vía grid → cells → cards. El scroll vive DENTRO de cada card, nunca
// acá. `min-height: 0` en body+grid+cell respeta el constraint a través de los
// layers (mismo patrón que BodySalesAnalysis).
.body_costs_analysis {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.state_message {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    text-align: center;
    color: var(--report-text-muted);
    font-size: 14px;
}

.error_text {
    color: var(--report-negative);
}

// Layout 2 columnas (no el 2×2 de Ventas): estado de costos a la izquierda,
// ranking de contrapartes a la derecha. Ambas cards a altura completa; cada
// una scrollea internamente su lista/estado.
.analysis_grid {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
}

.cell {
    min-width: 0;
    min-height: 0;
}
</style>
